// ActionFolder — "Your Roadmap to Action" folder content
import { ArrowRight } from "lucide-react";
import { ShieldAlert } from "lucide-react";

const STUDIO_LOGO = "https://customer-assets.emergentagent.com/job_4f723f94-5de1-433e-a1fc-17c2a953d5fd/artifacts/3r4ezkw8_Nehemiah%20Profile%201.png";

const ROADMAP_STEPS = [
  { phase: "1", title: "Get Expert Input", desc: "Book a free consultation to validate strategies and refine your plan", highlight: true },
  { phase: "2", title: "Plan & Design", desc: "Work with a footprint-first architect to create your improvement plan" },
  { phase: "3", title: "Implement", desc: "Execute your top strategy with professional guidance" },
  { phase: "4", title: "Realise Value", desc: "List, refinance, or simply enjoy your home's new potential" },
];

export function ActionFolder({ valueHeadroom, isUnlocked }) {
  const strategies = valueHeadroom?.strategies || [];
  const valueTraps = valueHeadroom?.value_traps || [];
  const top = strategies[0];

  return (
    <div className="space-y-4">
      {/* Priority Strategy */}
      {top && (
        <div className="bg-cipher-green/[0.06] border border-cipher-green/15 rounded-xl px-4 py-3" data-testid="priority-roadmap">
          <p className="text-[10px] text-cipher-green/60 uppercase tracking-wider mb-1.5">Your #1 Priority</p>
          <p className="text-sm text-white/85 font-medium">{top.label}</p>
          <div className="flex items-center gap-3 mt-2">
            {top.percentage_range && (
              <span className="text-[10px] text-cipher-green/70">+{top.percentage_range.lower}–{top.percentage_range.upper}% uplift</span>
            )}
            {top.implementation_range && (
              <span className="text-[10px] text-white/40">
                £{Math.round(top.implementation_range.lower / 1000)}k–£{Math.round(top.implementation_range.upper / 1000)}k est.
              </span>
            )}
          </div>
          <p className="text-[11px] text-white/40 mt-2 leading-relaxed">{top.description}</p>
        </div>
      )}

      {/* Visual Roadmap */}
      <div data-testid="action-roadmap">
        <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Your path forward</p>
        {ROADMAP_STEPS.map((step, i) => (
          <div key={step.phase} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${
                step.highlight ? 'bg-cipher-green text-white' : 'bg-white/10 text-white/40'
              }`}>{step.phase}</div>
              {i < ROADMAP_STEPS.length - 1 && <div className="w-px h-6 bg-white/10" />}
            </div>
            <div className="pb-3 flex-1">
              <p className={`text-xs font-medium ${step.highlight ? 'text-white' : 'text-white/60'}`}>{step.title}</p>
              <p className="text-[10px] text-white/35 leading-relaxed mt-0.5">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Things to watch */}
      {valueTraps.length > 0 && (
        <div className="bg-amber-500/[0.04] border border-amber-500/10 rounded-xl px-3.5 py-3" data-testid="risk-summary">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-3.5 h-3.5 text-amber-400/60" />
            <p className="text-[10px] text-amber-400/60 uppercase tracking-wider font-medium">Things to watch</p>
          </div>
          <div className="space-y-1.5">
            {valueTraps.map((t) => (
              <p key={t.id} className="text-xs text-amber-300/60 leading-relaxed">
                <span className="font-medium text-amber-300/80">{t.label}:</span> {t.description}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Professional consultation */}
      <div className="bg-gradient-to-r from-cipher-green/[0.08] to-cipher-gold/[0.06] border border-white/10 rounded-xl p-4" data-testid="studionpg-cta">
        <div className="flex items-center gap-3 mb-3">
          <img src={STUDIO_LOGO} alt="StudioNPG" className="w-10 h-10 rounded-lg" />
          <div>
            <p className="text-sm text-white/80 font-medium">StudioNPG</p>
            <p className="text-[10px] text-white/40">Footprint-First Property Intelligence</p>
          </div>
        </div>
        <p className="text-xs text-white/50 leading-relaxed mb-3">
          Every successful transformation starts with expert validation. StudioNPG specialises in turning ordinary homes into high-performing assets — without extensions.
        </p>
        <a
          href="https://studionpg.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-cipher-green/20 border border-cipher-green/30 text-cipher-green text-xs font-medium hover:bg-cipher-green/30 transition-colors"
          data-testid="studionpg-link"
        >
          Book a Free Consultation — No Obligation
        </a>
        <p className="text-[9px] text-white/25 text-center mt-2">Just clarity on your next step</p>
      </div>

      {/* What happens if you don't act */}
      <div className="bg-white/[0.02] border border-white/5 rounded-xl px-3.5 py-2.5">
        <p className="text-[10px] text-white/35 leading-relaxed">
          Properties that don't optimise rely solely on market growth. Targeted improvements can accelerate value creation beyond what the market alone delivers.
        </p>
      </div>
    </div>
  );
}
